import type { JSX } from 'react';
import { List, ListItem } from 'ts/components/List';
import type { FindingDetailCodeScrollHandles } from 'ts/perspectives/findings/details/FindingDetailView';
import type { StatementPathElement } from 'typedefs/StatementPathElement';
import type { TextRegionLocation } from 'typedefs/TextRegionLocation';
import type { TrackedFindingWithDiffInfo } from 'typedefs/TrackedFindingWithDiffInfo';

/** Renders source and sink locations for taint analysis findings. */
export function FindingLocationsOfTaintAnalysis({
	finding,
	scrollHandles
}: {
	finding: TrackedFindingWithDiffInfo;
	scrollHandles: FindingDetailCodeScrollHandles;
}): JSX.Element | null {
	if (finding.statementPath === undefined || finding.statementPath.length <= 1) {
		return null;
	}

	const sourceLocation = finding.statementPath[0]!.location as TextRegionLocation;
	const sinkIndex = getDataflowSinkIndex(finding.statementPath);
	const sinkLocation = finding.statementPath[sinkIndex]!.location as TextRegionLocation;

	return (
		<div className="word-break w-full mh-12 mt-2">
			<List bulleted>
				<ListItem>
					Source:{' '}
					<button
						type="button"
						className="teamscale-link-button"
						onClick={() =>
							scrollHandles.scrollToLine(sourceLocation.location!, sourceLocation.rawStartLine)
						}
					>
						{sourceLocation.location}:{sourceLocation.rawStartLine}
					</button>
				</ListItem>
				<ListItem>
					Sink:{' '}
					<button
						type="button"
						className="teamscale-link-button"
						onClick={() => scrollHandles.scrollToLine(sinkLocation.location!, sinkLocation.rawStartLine)}
					>
						{sinkLocation.location}:{sinkLocation.rawStartLine}
					</button>
				</ListItem>
			</List>
		</div>
	);
}

/**
 * Returns the sink of the given statement path. The sink is the node that has no successor node. If there are multiple
 * nodes without successors, we return the first. If there is no node without successors (cycle), we return -1.
 */
export function getDataflowSinkIndex(statementPath: StatementPathElement[]): number {
	const indicesOfNodesWithoutSuccessors = new Set<number>();
	statementPath.forEach((statementPathElement, index) => {
		indicesOfNodesWithoutSuccessors.add(index);
	});
	statementPath.forEach(statementPathElement => {
		statementPathElement.predecessors.forEach(predecessor => {
			indicesOfNodesWithoutSuccessors.delete(predecessor);
		});
	});
	if (indicesOfNodesWithoutSuccessors.size === 0) {
		return -1;
	}
	const element = indicesOfNodesWithoutSuccessors.keys().next().value;
	if (element == null) {
		return -1;
	} else {
		return element;
	}
}
